import moment from 'moment';

import BenchlingConfig from 'BenchlingConfig';
import {pageLoadID} from 'ClientHeaders';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ServerDocumentEditorLock} from 'notebook/realtime/DocumentEditorLockConstants';
import * as DocumentEditorLockDAO from 'notebook/realtime/DocumentEditorLockDAO';
import {isCurrentClient} from 'realtime/util/RealtimeUtil';
import {Thunk} from 'redux-stores/Store';
import EntityStore from 'stores/EntityStore';
import APIUtil from 'util/APIUtil';

const inFlightLockAcquisitions = new Set<string>();
const inFlightLockReleases = new Set<string>();

/**
 * Establishes a refractory period during which local document update actions will not result in attempts to
 * renew the editor lock from the server. The goal is to balance proactively maintaining the lock when a user
 * is editing over a period of time without spamming the server with lock refresh requests. Only applies when
 * the current client holds the lock.
 */
const lockRefreshWindowSeconds = Math.max(
  BenchlingConfig.ELN_REALTIME_DOCUMENT_EDITOR_LOCK_DURATION_SECONDS / 2,
  1
);

export enum DocumentEditorLockActionType {
  CLEAR_LOCK = 'CLEAR_LOCK',
  UPDATE_LOCK = 'UPDATE_LOCK',
}

interface BaseDocumentEditorLockAction {
  type: DocumentEditorLockActionType;
  fileId: string;
}

interface DocumentEditorLockUpdateAction extends BaseDocumentEditorLockAction {
  type: DocumentEditorLockActionType.UPDATE_LOCK;
  userId: string;
  clientId: string;
  msUntilExpiration: number;
}

interface DocumentEditorLockClearAction extends BaseDocumentEditorLockAction {
  type: DocumentEditorLockActionType.CLEAR_LOCK;
  force?: boolean;
}

const MODEL_CONSTANTS_SUPPORTING_REALTIME = [ModelTypeConstants.DOCUMENT, ModelTypeConstants.ENTRY];

function _modelTypeSupportsRealtime(apiId: string): boolean {
  return MODEL_CONSTANTS_SUPPORTING_REALTIME.includes(APIUtil.apiIdToType(apiId));
}

export function documentEditorLockUpdate(
  fileId: string,
  userId: string,
  clientId: string,
  msUntilExpiration: number
): DocumentEditorLockUpdateAction {
  return {
    type: DocumentEditorLockActionType.UPDATE_LOCK,
    fileId,
    userId,
    clientId,
    msUntilExpiration,
  };
}

export function documentEditorLockClear(fileId: string, force?: boolean): DocumentEditorLockClearAction {
  return {
    type: DocumentEditorLockActionType.CLEAR_LOCK,
    fileId,
    force,
  };
}

export type DocumentEditorLockAction = DocumentEditorLockUpdateAction | DocumentEditorLockClearAction;

export function acquireLocks(documentIds: Array<string>, forceNetwork?: boolean): Thunk<void> {
  return (dispatch, getState) => {
    const documentsToAcquireLocksFor = documentIds.filter((documentId) => {
      // Skip acquiring the lock and noop if this document type does not support realtime OR
      // someone else alreadys owns the lock or we own it and it's not expiring soon
      const existingLock = getState().ui.documentEditorLockState[documentId];
      const currentClientCanRefreshLock =
        existingLock &&
        isCurrentClient(existingLock?.clientId) &&
        moment().add(lockRefreshWindowSeconds, 'seconds').isAfter(moment(existingLock.expiresAt));

      return (
        _modelTypeSupportsRealtime(documentId) &&
        !inFlightLockAcquisitions.has(documentId) &&
        (!existingLock || currentClientCanRefreshLock)
      );
    });

    // We acquire the lock on the backend as part of the patch document endpoint unless forced to send out a
    // separate lock acquisition request.
    if (!forceNetwork) {
      for (const documentId of documentsToAcquireLocksFor) {
        dispatch(
          documentEditorLockUpdate(
            documentId,
            EntityStore.currentUserId ?? '',
            pageLoadID,
            BenchlingConfig.ELN_REALTIME_DOCUMENT_EDITOR_LOCK_DURATION_SECONDS * 1000
          )
        );
      }
    } else {
      for (const documentId of documentsToAcquireLocksFor) {
        inFlightLockAcquisitions.add(documentId);
      }

      if (documentsToAcquireLocksFor.length > 0) {
        DocumentEditorLockDAO.acquireLocks(documentsToAcquireLocksFor)
          .then((locks: Record<string, ServerDocumentEditorLock> | null) => {
            if (locks) {
              for (const [documentId, lock] of Object.entries(locks)) {
                dispatch(
                  documentEditorLockUpdate(documentId, lock.userId, lock.clientId, lock.msUntilExpiration)
                );
              }
            }
          })
          .catch(() => {})
          .finally(() => {
            for (const documentId of documentsToAcquireLocksFor) {
              inFlightLockAcquisitions.delete(documentId);
            }
          });
      }
    }
  };
}

export function releaseLocks(documentIds: Array<string>): Thunk<void> {
  return (dispatch, getState) => {
    const documentsToReleaseLocksFor: Array<string> = [];
    for (const documentId of documentIds) {
      if (inFlightLockReleases.has(documentId)) {
        continue;
      }
      const existingLock = getState().ui.documentEditorLockState[documentId];
      if (
        !existingLock ||
        !isCurrentClient(existingLock.clientId) ||
        moment().add(lockRefreshWindowSeconds, 'seconds').isAfter(moment(existingLock.expiresAt))
      ) {
        continue;
      }
      documentsToReleaseLocksFor.push(documentId);
      inFlightLockReleases.add(documentId);
    }
    if (documentsToReleaseLocksFor.length === 0) {
      return;
    }

    DocumentEditorLockDAO.releaseLocks(documentsToReleaseLocksFor)
      .then((locks: Record<string, ServerDocumentEditorLock> | null) => {
        if (locks) {
          for (const [documentId, _lock] of Object.entries(locks)) {
            dispatch(documentEditorLockClear(documentId, true));
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        for (const documentId of documentsToReleaseLocksFor) {
          inFlightLockReleases.delete(documentId);
        }
      });
  };
}

export function releaseLock(documentId: string): Thunk<void> {
  return (dispatch, getState) => {
    // If someone else alreadys owns the lock or we own it and it's not expiring soon, noop
    const existingLock = getState().ui.documentEditorLockState[documentId];
    // If there is no lock, the current client does not hold the lock, or if the lock has already
    // expired or there is a request already in flight, noop
    if (
      !existingLock ||
      !isCurrentClient(existingLock.clientId) ||
      moment().isAfter(moment(existingLock.expiresAt))
    ) {
      return;
    }

    // if there is already a request in flight, noop
    if (inFlightLockReleases.has(documentId)) {
      return;
    }

    inFlightLockReleases.add(documentId);
    DocumentEditorLockDAO.releaseLock(documentId)
      .then((lock: ServerDocumentEditorLock | null) => {
        if (lock) {
          dispatch(documentEditorLockClear(documentId, true));
        }
      })
      .catch(() => {})
      .finally(() => {
        inFlightLockReleases.delete(documentId);
      });
  };
}
